import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle2 } from 'lucide-react';
import SEO from '@/components/SEO';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Terms & Conditions"
        description="Review the terms and conditions for using Ixoraly's press release distribution services. Understand your rights and obligations."
        path="/terms-conditions"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <FileText className="w-8 h-8 text-coral-500" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="terms-heading">
              Terms & Conditions
            </h1>
            <p className="text-gray-300">Last updated: February 2026</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Welcome to <strong>Ixoraly</strong>. By accessing or using our press release distribution services, you agree to comply with and be bound by the following terms and conditions.
            </p>

            <div className="bg-navy-50 rounded-xl p-8 mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700">
                By using Ixoraly's services, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions. If you do not agree with any part of these terms, you should not use our services.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
              <p className="text-gray-700 mb-4">
                Ixoraly provides press release distribution services that include:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="text-green-600 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Publication on Ixoraly newsroom</span>
                </div>
                <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="text-green-600 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Distribution to media outlets</span>
                </div>
                <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="text-green-600 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Search engine indexing</span>
                </div>
                <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="text-green-600 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Social media sharing</span>
                </div>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
              <p className="text-gray-700 mb-4">When submitting a press release, you agree to:</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Provide accurate and truthful information</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Own or have the right to distribute the content</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Not submit content that is defamatory, obscene, or violates any laws</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Respect intellectual property rights and copyright laws</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Not use automated or bulk submission software</span>
                </li>
              </ul>
            </div>

            <div className="bg-navy-50 rounded-xl p-8 mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Content Guidelines</h2>
              <p className="text-gray-700 mb-4">Ixoraly reserves the right to reject or remove press releases that:</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Contain adult, explicit, or inappropriate content</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Promote illegal activities or substances</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Include spam or excessive promotional links (maximum 5 backlinks)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Violate copyright or trademark rights</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Are repetitive or generated through automated systems</span>
                </li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Payment must be completed before distribution begins</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>All prices are in USD unless otherwise specified</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Refunds are not provided once distribution has begun</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>We reserve the right to change pricing with notice</span>
                </li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
              <p className="text-gray-700">
                You retain all rights to your submitted content. By submitting a press release, you grant Ixoraly a non-exclusive, worldwide license to publish and distribute your content through our network.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                Ixoraly provides distribution services on an "as is" basis. We do not guarantee:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Specific media coverage or pickup</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Search engine rankings or SEO results</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Traffic, sales, or business outcomes</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Ixoraly is not liable for any indirect, incidental, or consequential damages arising from the use of our services.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Termination</h2>
              <p className="text-gray-700">
                We reserve the right to terminate or suspend access to our services at any time, without prior notice, for conduct that we believe violates these Terms & Conditions or is harmful to other users or our business.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to Terms</h2>
              <p className="text-gray-700">
                Ixoraly reserves the right to modify these Terms & Conditions at any time. Changes will be effective immediately upon posting. Your continued use of our services constitutes acceptance of the updated terms.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-700">
                These Terms & Conditions are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Pune, Maharashtra.
              </p>
            </div>

            <div className="bg-gradient-to-br from-navy-600 to-navy-700 rounded-xl p-8 text-white">
              <h3 className="font-bold text-xl mb-3">Questions About These Terms?</h3>
              <p className="text-navy-100 mb-4">
                If you have any questions about these Terms & Conditions, please contact us.
              </p>
              <a href="mailto:contact@ixoraly.com" className="inline-flex items-center px-6 py-3 bg-white text-navy-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                contact@ixoraly.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
